/**
 * One confirmation UI for the application. Transport and authorization remain
 * owned by the caller (FormZero, navigation, a normal POST form, or TippyConfirm).
 * Server-provided messages are always inserted as text, never as HTML.
 */
export default class ConfirmationDialog {
    constructor(translate = (text) => text) {
        this.translate = translate;
        this.active = false;
    }

    ask(message, trigger = null, options = {}) {
        if (this.active) return Promise.resolve(false);
        this.active = true;
        const destructive = options.destructive !== false;
        const text = (key, fallback) => {
            const translated = this.translate(key);
            return translated && translated !== key ? translated : fallback;
        };
        const title = options.title || (destructive
            ? text('confirm.deleteTitle', 'Conferma eliminazione')
            : text('confirm.title', 'Conferma operazione'));
        const approveLabel = options.approveLabel || (destructive
            ? text('confirm.delete', 'Elimina')
            : text('confirm.proceed', 'Conferma'));
        const cancelLabel = options.cancelLabel || text('confirm.cancel', 'Annulla');
        const note = options.note === false || !destructive ? ''
            : options.note || text('confirm.irreversible', 'Questa operazione non può essere annullata.');
        const fallback = () => {
            try { return typeof window !== 'undefined' && window.confirm?.(message) === true; }
            finally { this.active = false; }
        };
        if (typeof document === 'undefined' || !document.body?.append ||
            typeof document.createElement !== 'function') {
            return Promise.resolve(fallback());
        }
        const dialog = document.createElement('dialog');
        if (typeof dialog.showModal !== 'function') return Promise.resolve(fallback());

        dialog.className = 'app-confirm-dialog';
        const titleId = 'app-confirm-dialog-title';
        const messageId = 'app-confirm-dialog-message';
        dialog.setAttribute('aria-labelledby', titleId);
        dialog.setAttribute('aria-describedby', messageId);
        const body = document.createElement('div');
        body.className = 'app-confirm-dialog__body';
        const heading = document.createElement('h2');
        heading.id = titleId;
        heading.className = 'app-confirm-dialog__title';
        heading.textContent = title;
        const description = document.createElement('p');
        description.id = messageId;
        description.className = 'app-confirm-dialog__message';
        description.textContent = String(message || '');
        const warning = document.createElement('p');
        warning.className = 'app-confirm-dialog__note';
        warning.textContent = note;
        if (!note) warning.hidden = true;
        const actions = document.createElement('div');
        actions.className = 'app-confirm-dialog__actions';
        const cancel = document.createElement('button');
        cancel.type = 'button';
        cancel.className = 'btn btn-outline-secondary';
        cancel.textContent = cancelLabel;
        const approve = document.createElement('button');
        approve.type = 'button';
        approve.className = destructive ? 'btn btn-danger' : 'btn btn-primary';
        approve.textContent = approveLabel;
        actions.append(cancel, approve);
        body.append(heading, description, warning, actions);
        dialog.append(body);
        const previousFocus = trigger ?? document.activeElement;
        return new Promise((resolve) => {
            let finished = false;
            const finish = (accepted) => {
                if (finished) return;
                finished = true;
                this.active = false;
                if (dialog.open) dialog.close();
                dialog.remove();
                if (previousFocus?.isConnected !== false) previousFocus?.focus?.();
                resolve(accepted);
            };
            cancel.addEventListener('click', () => finish(false));
            approve.addEventListener('click', () => finish(true));
            dialog.addEventListener('cancel', (event) => {event.preventDefault(); finish(false);});
            dialog.addEventListener('close', () => finish(false));
            document.body.append(dialog);
            try {
                dialog.showModal();
                cancel.focus(); // Always start on the safe action.
            } catch (_) {
                finish(typeof window !== 'undefined' && window.confirm?.(message) === true);
            }
        });
    }
}
